import React, {Fragment} from 'react';
import { deepCopy, safeParse, safeStringify, isObject, exists } from '../../../util';
import { getParentPath, getArrayIndexForPath, getObjectForPath, fieldNameHasReservedChars, objectPathExists, fieldPathForAction } from './util';
import EditDialogs, { dialogForAction } from './dialogs';
import { defaultValueFor } from './types';
import FieldActions from './fieldactions';
import dset from 'dset';
import dlv from 'dlv';
import FieldEditView from './fieldeditview';
import Text from '../text';
import { dataPathToDataDescriptorPath } from './fieldeditview/util';

function applyAddFieldAction(data, fieldAction) {
	const parentPath = fieldAction.path;
	const parentObj = getObjectForPath(data, parentPath);
	const parentIsArray = Array.isArray(parentObj);
	const value = exists(fieldAction.args.fieldValue) ? fieldAction.args.fieldValue : defaultValueFor(fieldAction.args.fieldType);
	if(parentIsArray) {
		parentObj.push(value);
	} else {
		const path = `${(`${parentPath ? `${parentPath}.` :'' }` || "")}${fieldAction.args.fieldName}`;
		dset(data, path, value);
	}
	
	return data;
}

function applyDeleteFieldAction(data, fieldAction) {
	const path = fieldAction.path;
	const parentPath = getParentPath(path);
	const parentObject = getObjectForPath(data, parentPath);
	const parentIsArray = Array.isArray(parentObject);
	if(parentIsArray) {
		const index = getArrayIndexForPath(path);
		parentObject.splice(index,1);

	} else {
		dset(data, path, undefined);
	}
	return data;
}

function applyRenameFieldAction(data, fieldAction) {
	const path = fieldAction.path;
	const value = dlv(data, path);
	const parentPath = getParentPath(path);
	const parentObject = getObjectForPath(data, parentPath);
	const parentIsArray = Array.isArray(parentObject);
	if(parentIsArray) {
		const index = getArrayIndexForPath(path);
		parentObject.splice(index,1);
	} else {
		dset(data, path, undefined);
	}
	dset(data, `${parentPath ? `${parentPath}.` : ''}${fieldAction.args.fieldName}`, value);
	return data;
}

function applyUpdateFieldAction(data, fieldAction) {
	const path = fieldAction.path;
	const value = fieldAction.args.value;
	dset(data, path, value);
	return data;
}

function applyCopyFieldAction(data, fieldAction) {
	const target = dlv(data, fieldAction.path);
	const value = isObject(target) ? JSON.stringify(target) : target;
	navigator.clipboard.writeText(value);
	return data;
}

const fieldActionApplyFunctions = {
	[FieldActions.Add]:applyAddFieldAction,
	[FieldActions.Delete]:applyDeleteFieldAction,
	[FieldActions.Rename]:applyRenameFieldAction,
	[FieldActions.Update]:applyUpdateFieldAction,
	[FieldActions.Copy]:applyCopyFieldAction
}

function applyFieldActionTo(data, fieldAction) {
	if(!fieldAction || !fieldAction.action) return data;
	const f = fieldActionApplyFunctions[fieldAction.action];
	if(f) return f(data, fieldAction);
	return data;
}

const ObjectEditor = React.memo(({ defaultValue, label, onChange, collapsible, title, dataDescriptor }) => {
	const defaultAsJson = defaultValue ? safeStringify(defaultValue) : null;
	const [storedDefault, setStoredDefault] = React.useState(defaultAsJson);
	const [workingData, setWorkingData] = React.useState(safeParse((defaultAsJson || storedDefault || null)));	
	const [fieldActionRequest, setFieldActionRequest] = React.useState({});
	const [dataPath, setDataPath] = React.useState('');
	const [objZoomed, setObjZoomed] = React.useState(false);

	const setWorkingDataCopy = val => setWorkingData(deepCopy(val));

	const executeFieldAction = (workingData, actionToExecute) => {
		const newData = applyFieldActionTo(workingData, actionToExecute);
		setWorkingDataCopy(newData);
		if(onChange) onChange(newData);
	}

	const handleDialogClose = (response) => {
		if(response) {
			const { args:requestArgs, ...request } = fieldActionRequest;
			const actionToExecute = {...request, args:{...requestArgs, ...response}};
			executeFieldAction(workingData, actionToExecute);
		}
		setFieldActionRequest({});
	}
	
	const handleFieldActionRequest = (action, path, type, args) => {
		const actionToExecute = {action, path, type, args};
		if(!dialogForAction(action) || dlv(args, 'injectDataDirectly')) {
			executeFieldAction(workingData, actionToExecute);
		} else {
			setFieldActionRequest(actionToExecute);
		}
	}

	const handleObjZoomRequest = (dataPath, objZoomed) => {
		setDataPath(dataPath);
		setObjZoomed(objZoomed);
	}

	const validateFieldPath = fieldName => {
		let passed, msg;

		// We'll proceed if parent is a non-array item since for array, path is decided by JS runtime.
		if (exists(fieldName)) {
			const fName = fieldName.trim();

			if (fName === "") {
				msg = 'Please enter a field name.';
			} else if (!fieldNameHasReservedChars(fName)) {
				msg = 'Field name should not contain these characters: . (dot), [ (left square bracket), ] (right square bracket)';
			} else if (objectPathExists(workingData, fieldPathForAction(fName, fieldActionRequest))) {
				msg = `Caution! \`${fName}\` already exists.`;
			}

			passed = !msg; // I know this line is weird. But thought to avoid `passed = false` line everytime path is invalid.
		}

		return { passed, msg };
	}

	const findDataForPath = path => getObjectForPath(workingData, path)

	React.useEffect(() => {
		if((defaultAsJson && defaultAsJson !== storedDefault)) {
			setStoredDefault(defaultAsJson);
			setWorkingData(safeParse(defaultAsJson));
		}
	}, [storedDefault, defaultAsJson]);

	return (
		<Fragment>
			{title && <Text variant="h5">{title}</Text>}
			<FieldEditView 
				value={dataPath ? dlv(workingData, dataPath) : workingData} 
				label={label} 
				collapsible={collapsible || false} 
				onFieldActionRequest={handleFieldActionRequest}
				dataPath={dataPath} 
				objZoomed={objZoomed}
				onObjZoomRequest={handleObjZoomRequest}
				dataDescriptor={dataPath ? dlv(dataDescriptor, dataPathToDataDescriptorPath(dataPath)) : dataDescriptor}
			/>
			<EditDialogs
				fieldActionRequest={fieldActionRequest}
				onClose={handleDialogClose}
				validateFieldPath={validateFieldPath}
				findDataForPath={findDataForPath}
			/>
		</Fragment>
	);
});

export default ObjectEditor;